// matomo.service.ts
import {Injectable} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {debounceTime, filter} from 'rxjs/operators';
import {Store} from "@ngrx/store";
import {NavState} from "../../store/navigation/navigation.reducer";
import {BehaviorSubject, distinctUntilChanged, lastValueFrom, Subscription} from "rxjs";
import {Actions} from '@ngrx/effects';
import {touchpoint, url} from '../../store/navigation/navigation.action';
import {selectNav} from "../../store/navigation/navigation.selector";
import {CookieService, CookieStatus} from "./cookie.service";
import _default from "chart.js/dist/plugins/plugin.decimation";
import {AuthenticationService} from "./authentication.service";
import {environment} from "../../../environments/environment";


declare global {
  interface Window {
    _paq: any[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class MatomoService {
  private subscritions: Subscription[] = [];
  private matomoLoaded = false;

  constructor(private router: Router, private store: Store<NavState>, private actions$: Actions,
              private cookieService: CookieService, private authenticationService: AuthenticationService) {

    window._paq = window._paq || [];

    this.setupSubscriptions();
  }


  private setupSubscriptions(): void {
    const cookiePolicySub =  this.cookieService.cookiePolicySubject.subscribe(value => {
      if (value === CookieStatus.ACCEPTED) {
        window._paq.push(['setConsentGiven']);
        window._paq.push(['rememberConsentGiven']);
        window._paq.push(['setCookieConsentGiven']);
        window._paq.push(['rememberCookieConsentGiven']);
        window._paq.push(['trackPageView']);
      }
      if (value === CookieStatus.REJECTED) {
        window._paq.push(['forgetConsentGiven']);
        window._paq.push(['forgetCookieConsentGiven']);
      }
      if (value === CookieStatus.NOT_EVALUATED) {
        window._paq.push(['requireConsent']);
        window._paq.push(['requireCookieConsent']);
      }
    });
    const authSub = this.authenticationService._userSubject.subscribe(user => {
      if (this.handleUserID(user)) {
        this.matomoInit(user);
          this.trackEvent('INIT', 'matomo first call', "user logged in");
          const logintrack = "/login [login][][]";
          this.trackPageView(logintrack);

      } else {
        this.destroy();
      }


    });
    this.subscritions.push(cookiePolicySub,authSub)
  }




  matomoInit(user: any): void {



    this.trackFirstPageLoadTime();

    const navSub = this.store.select(selectNav)
      .pipe(
        debounceTime(300),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe(currentState => {
        //const state = `url:${currentState.url} [touchpoint:${currentState.touchpoint}][leftmenu:${currentState.leftMenu}][tab${currentState.tabitem}]`;
        let touchpoint = currentState.touchpoint == "AFTERSALES" ? "REPAIR" : currentState.touchpoint;
        touchpoint = touchpoint == "SALES" ? "PURCHASE"  : touchpoint;
        touchpoint = touchpoint == "DEMO" ? "CONSIDERATION"  : touchpoint;
        let leftMenu = currentState.url.indexOf("analysis") > -1 ? 'analysis' : currentState.leftMenu;
        leftMenu = leftMenu.toLowerCase();
        leftMenu = leftMenu == "home" ? "Journey"  : leftMenu;
        leftMenu = leftMenu == "recovery-plan" ? "Alert management"  : leftMenu;

        if(["dashboard","download","reporting","video-tutorial","journey"].indexOf(leftMenu) > -1 ){
          touchpoint = '';
        }
        const state = `${currentState.url} [${leftMenu}][${touchpoint}][${currentState.tabitem}]`;
        console.log("************** MATOMO STATE ******************");
        console.log(currentState);
        console.log(state);
        console.log("**********************************************");
        this.trackPageView(state);
        this.trackPageLoadTimeOnNavigation();
      })

    const routerSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.store.dispatch(url({item: this.router.url}))
    });

    this.subscritions.push(navSub,routerSub)

  }


  private paqPush(args: any[]) {
    if (!!this.authenticationService._userSubject.value) {
      this.handleUserID(this.authenticationService._userSubject.value);
    }
    window._paq.push(args);
  }


  trackPageView(customTitle?: string): void {
    if (customTitle) {
      this.paqPush(['setDocumentTitle', customTitle]);
    }
    //this.paqPush(['appendToTrackingUrl', '']);
    this.paqPush(['trackPageView']);

  }

  trackFirstPageLoadTime(): void {

    if (window.performance && window._paq) {
      window.onload = () => {
        const navigationTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        const loadTime = navigationTiming ? navigationTiming.loadEventEnd - navigationTiming.startTime : 0;
        //window._paq.push(['trackEvent', 'Performance', 'Page Load Time', window.location.pathname, loadTime]);
        this.paqPush(['trackEvent', 'Performance', 'Page Load Time', window.location.pathname, loadTime]);
        // Or:
        // window._paq.push(['setCustomMetric', 'your_metric_id', loadTime]);
      };
    }
  }

  private trackPageLoadTimeOnNavigation(): void {
    if (window.performance && window._paq) {
      const navigationStart = 0;

      // Osserva le richieste di rete
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntriesByType('resource') as PerformanceResourceTiming[];
        const apiCalls = entries.filter(entry => entry.initiatorType === 'xmlhttprequest' || entry.initiatorType === 'fetch');

        const navigationTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        const loadTime = navigationTiming ? navigationTiming.loadEventEnd - navigationTiming.startTime : 0;

        const apiLoadTime = apiCalls.reduce((total, entry) => total + (entry.responseEnd - entry.startTime), 0);
        const totalLoadTime = Math.round(loadTime + apiLoadTime);

        // Traccia il tempo di caricamento totale
        //window._paq.push(['trackEvent', 'Performance', 'Page Load Time (with API)', window.location.pathname, totalLoadTime]);
        this.paqPush(['trackEvent', 'Performance', 'Page Load Time (with API)', window.location.pathname, totalLoadTime]);
        observer.disconnect(); // Disconnetti l'osservatore
      });

      observer.observe({entryTypes: ['resource']});
    }
  }

  trackEvent(category: string, action: string, name?: string, value?: number): void {

    //window._paq.push(['trackEvent', category, action, name, value]);
    this.paqPush(['trackEvent', category, action, name, value]);

  }

  private destroy() {
    this.trackEvent('Logout', 'user logout', 'session ended');
    this.subscritions.forEach(sub => sub.unsubscribe());
  }

  private handleUserID(user: any): boolean {
    if (!!user && (!!user.email || !!user.csiProfile)) {
      window._paq.push(['setUserId', this.userDetails(user)]);
      return true;
    }
    return false;
  }

  private userDetails(user: any) {
    return `${user.email} | ${user.csiProfile}`;
  }
}
